import moment from 'moment'
import React, { useState, useCallback, useMemo } from 'react'
import { set, useFormContext, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
// Util

// UI
import { Flex, Input, Select, Text } from '@chakra-ui/react'
// Custom

//Slice
import { setFileHistoryModal } from '@/stores/ui/uiSlice'

//Constant
import { MultipleFileViewCell } from '@/components/Table/components/Cell'
import EditableTable from '@/components/Table/EditableTable'
import { UncontrolledDatePicker } from '@/components/UncontrolledForm'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { useDelegation } from '@/features/delegations/useDelegation'
import { ApprovalStatus, OfferDocumentTypes } from '@/features/offers/constants'
import { offerQuotationGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerQuotationEditRights'
import { selectOfferDetail, selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import useUser from '@/features/users/useUser'
import { authSelector } from '@/stores/auth/authSlice'
import { original } from '@reduxjs/toolkit'
import { createColumnHelper } from '@tanstack/react-table'
import {
  APPROVAL_TABLE_TYPE,
  buildApproverTableColumn,
  getInvitationButtonText,
} from '@/features/offers/offer-detail/helper/approverTableColumn.helper'

const ApproverTable = ({ setSelectedApprover }) => {
  // Redux
  const { adminMode } = useSelector(selectOfferDetail)
  const offerPermissions = useSelector(selectOfferPermissions)
  const { user } = useSelector(authSelector)

  // Hooks
  const { setValue, getValues } = useFormContext()
  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()

  const createdBy = useWatch({ name: 'createdBy' })
  const approvers = useWatch({ name: 'approvers' })

  const profitCenterId = useWatch({ name: `subSystems.0.profitCenter._id` })
  const offerSubmissionDate = useWatch({ name: `offerSubmissionDate` })
  const status = useWatch({ name: `status` })

  const { useFetchCostCenter } = useCostCenter()
  const { useFetchAllDelegations } = useDelegation()
  const { useFetchAllUsers } = useUser()

  const { data: profitCenterResponse } = useFetchCostCenter(profitCenterId)
  const { data: allDelegationResponse } = useFetchAllDelegations({
    page: 1,
    pageSize: 0,
  })
  const { data: userResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    sortBy: 'fullName',
    orderBy: 1,
  })

  const canEditApproval = offerPermissions[offerQuotationGroupPolicyType.QUOTATION_APPROVAL]?.edit ?? false

  const approverTableData = useMemo(() => {
    if (!approvers || !Array.isArray(profitCenterResponse?.approvers) || !Array.isArray(allDelegationResponse?.data))
      return []

    return approvers.map((approverDetail) => {
      // approverDetail.user may be a populated object {_id, firstName, lastName}
      // (the populate omits the fullName virtual) OR a bare id string after
      // formatOfferFromServer rewrites it. Resolve the id and the display name
      // from the all-users list so the name shows in read-only (non-admin) mode
      // too — the same all-users source ContributorTable uses.
      const approverUserId =
        typeof approverDetail?.user === 'string' ? approverDetail.user : approverDetail?.user?._id
      const resolvedUser = Array.isArray(userResponse?.data)
        ? userResponse.data.find((u) => u._id === approverUserId)
        : undefined
      const approverUserName = approverDetail?.user?.fullName || resolvedUser?.fullName || ''

      const userOptions = profitCenterResponse.approvers
        .filter(
          (costCenterUserDetail) =>
            costCenterUserDetail?.type === approverDetail?.type &&
            costCenterUserDetail?.level === approverDetail?.level &&
            costCenterUserDetail?.user?._id &&
            costCenterUserDetail?.user?._id !== '' &&
            costCenterUserDetail?.user?._id !== createdBy?._id
        )
        .map((approver) => ({
          label: approver?.user?.fullName || '',
          value: approver?.user?._id || '',
        }))

      // Keep the assigned approver selectable + labeled even if not a configured
      // cost-center approver for this level/type (e.g. assigned via the API).
      if (approverUserId && !userOptions.some((option) => option.value === approverUserId)) {
        userOptions.unshift({ label: approverUserName, value: approverUserId })
      }

      let delegationRemark = ''
      let isEffective = true

      if ([ApprovalStatus.APPROVED, ApprovalStatus.REJECTED].includes(approverDetail?.status)) {
        if (approverDetail?.lastActionUser?._id && approverDetail?.lastActionUser?._id !== approverDetail?.user?._id) {
          delegationRemark = `${approverDetail?.status} by delegatee, ${approverDetail?.lastActionUser?.fullName}`
        }
      } else {
        const activeDelegation = allDelegationResponse.data
          .sort((a, b) => new Date(a.validStartDate) - new Date(b.validStartDate))
          .find((row) => {
            const validStartDate = moment(row.validStartDate)
            const validEndDate = moment(row.validEndDate)

            if (!validStartDate || !validStartDate.isValid() || !validEndDate || !validEndDate.isValid()) {
              return false
            }
            //Not started workflow and the delegator is this approver
            return (
              row.delegatedCostCenter.includes(profitCenterId) &&
              //find delegation that is not yet ended
              validEndDate.isSameOrAfter(moment(), 'days') &&
              row.delegator === approverDetail?.user?.fullName
            )
          })

        if (activeDelegation && activeDelegation?.delegatee) {
          delegationRemark = `Delegated to ${activeDelegation?.delegatee} from ${moment(activeDelegation?.validStartDate).format('DD-MMM-YYYY')} to ${moment(activeDelegation?.validEndDate).format('DD-MMM-YYYY')}`
          isEffective =
            activeDelegation &&
            moment().isBetween(activeDelegation.validStartDate, activeDelegation.validEndDate, 'day', '[]')
        }
      }

      //Invitation
      const invitedUsers = Array.isArray(approverDetail.invitedUsers) ? approverDetail.invitedUsers : []
      const invitationButtonText = getInvitationButtonText(invitedUsers)

      return {
        type: `${approverDetail?.level}${approverDetail?.type.charAt(0).toUpperCase()}`,
        user: approverUserId,
        userId: approverUserId,
        userName: approverUserName,
        userOptions: userOptions,
        dueDate: approverDetail.dueDate,
        maxDueDate: offerSubmissionDate ? new Date(offerSubmissionDate) : undefined,
        status: approverDetail?.status,
        approvedDate: approverDetail?.approvedDate,
        remark: approverDetail?.remark,
        delegationRemark: delegationRemark,
        isEffective: isEffective,
        attachments: approverDetail?.attachments,

        disableEditType: true,
        disableEditUser: false, //Use overall edit right
        disableEditDueDate: [ApprovalStatus.APPROVED, ApprovalStatus.REJECTED].includes(approverDetail.status),
        disableEditRemark: false, //Use overall edit right
        disableRemoveApprover: true,

        //Invitation
        invitedUsers: invitedUsers,
        invitationButtonText: invitationButtonText,
        canManageInvitations: String(approverUserId || '') === String(user?._id || ''),
      }
    })
  }, [
    adminMode,
    allDelegationResponse?.data,
    approvers,
    createdBy?._id,
    offerSubmissionDate,
    profitCenterResponse?.approvers,
    status,
    userResponse?.data,
  ])

  const updateApproverTableData = useCallback(
    (rowIndex, columnId, value) => {
      switch (columnId) {
        case 'user':
          {
            const chosenUser = Array.isArray(userResponse?.data)
              ? userResponse.data.find((user) => user._id === value)
              : undefined
            if (chosenUser) {
              setValue(`approvers.${rowIndex}.user`, chosenUser, {
                shouldDirty: true,
              })
            } else {
              setValue(`approvers.${rowIndex}.user`, { _id: '' }, { shouldDirty: true })
            }
          }

          break
        case 'dueDate':
        case 'status':
        case 'remark':
          setValue(`approvers.${rowIndex}.${columnId}`, value, {
            shouldDirty: true,
          })
          break

        default:
          throw new Error(`[CLIENT] Unexpected Error: unhandled column id update ${columnId}! `)
      }
    },
    [userResponse?.data, setValue]
  )

  const columnDef = useMemo(
    () =>
      buildApproverTableColumn({
        columnHelper,
        dispatch,
        tableType: APPROVAL_TABLE_TYPE.QUOTATION_APPROVAL,
        updateInvitationParameter: {
          offerId: getValues('_id'),
        },
      }),
    [columnHelper, dispatch, getValues]
  )

  if (!approvers) {
    return <></>
  }
  return (
    <EditableTable
      columnDef={columnDef}
      data={approverTableData}
      updateData={updateApproverTableData}
      disabled={!canEditApproval}
      enableRowSelection={true}
      onRowSelect={setSelectedApprover}
      tableWidth="100%"
    />
  )
}

export default ApproverTable
